var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef, } from 'react';
import { useImmer } from 'use-immer';
import { createDefaultNodeData } from '../../api/models'; // Removed NodeCoordinates import
import { getLevelById, updateLevel, submitLevel, submitImages } from '../../api/levels'; // Keep updateLevel maybe? Added updateNodeData
import { MapGrid } from '../MapGrid/MapGrid';
import { FaPlus, FaMinus, FaTimes, FaChevronDown, FaCheck, FaSave, FaExclamationTriangle, FaSync, FaUndo, FaRedo, FaStamp, FaPlay, FaClipboardCheck, FaBalanceScale, FaGraduationCap, FaLock } from 'react-icons/fa'; // Added icons
import SliderInput from '../SliderInput/SliderInput'; // Import SliderInput
import './LevelEditor.scss';
import { getCoordinateString } from '../../utils/coordinates';
import { thumb, assetUrl } from '../../utils/img';
import { KnowledgeReward } from '../../utils/gameIcons';
import { resetLevelToDefault } from '../../api/import';
import FloatingPanel from '../FloatingPanel/FloatingPanel'; // Import FloatingPanel
import NodePropertiesForm from '../NodePropertiesForm/NodePropertiesForm'; // Import the new form component
import { EventEditor } from '../EventEditor/EventEditor';
import { UxPanelEditor } from '../UxPanelEditor/UxPanelEditor';
import { StampTool } from '../StampTool/StampTool';
import { ScenarioManager } from '../ScenarioEditor/ScenarioManager';
import { DialogueEditor } from '../DialogueEditor/DialogueEditor';
import { ArtManager } from '../ArtManager/ArtManager';
import { MissionEditor } from '../MissionEditor/MissionEditor';
import { PhonePreview } from '../PlayModeSettings/PhonePreview';
import { backendUrl, levelEditLock, PrizeType, DEFAULT_TRIAL_DIFFICULTY, ModerationStatus, MAP_PADDING_CELLS, MAP_PADDED_CELLS } from '@common/contracts';
import { getNodeImages } from '../../api/images';
import { getLevelEvents, updateEvent } from '../../api/level-events';
import AIGenerationPanel from '../AIGenerationPanel/AIGenerationPanel';
import OnboardingChecklist from '../OnboardingChecklist/OnboardingChecklist';
import Tutorial from '../Tutorial/Tutorial';
import { EDITOR_TOUR } from '../Tutorial/tours';
import { getOnboardingStatus, markEditorTutorialSeen } from '../../api/onboarding';
import DistributionPanel from '../DistributionPanel/DistributionPanel';
import VerificationPanel from '../VerificationPanel/VerificationPanel';
import PlayMode from '../PlayMode/PlayMode';
import { runVerification, runRepair } from '../../api/verification';
import { parseCoord } from '../DistributionPanel/distributionModel';
import Review from '../Review/Review'; // Review component
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { BACK_SENTINEL, useEditorUnsavedGuard } from '../../hooks/useEditorUnsavedGuard';
import ItemsManager from '../ItemsManager/ItemsManager';
import { getNodeTypes } from '../../api/node-types';
import { getNodeStatuses } from '../../api/node-statuses';
// Enumeration for save status
var SaveStatus;
(function (SaveStatus) {
    SaveStatus["Saved"] = "saved";
    SaveStatus["Saving"] = "saving";
    SaveStatus["Unsaved"] = "unsaved";
    SaveStatus["Error"] = "error";
})(SaveStatus || (SaveStatus = {}));
// Updated Color Options for CustomSelect
const COLOR_OPTIONS = [
    { value: '#000000', label: 'Black' },
    { value: '#FFFFFF', label: 'White' },
    { value: '#808080', label: 'Gray' },
    { value: '#FF0000', label: 'Red' },
    { value: '#00FF00', label: 'Lime' },
    { value: '#0000FF', label: 'Blue' },
    { value: '#FFFF00', label: 'Yellow' },
    { value: '#00FFFF', label: 'Cyan' },
    { value: '#FF00FF', label: 'Magenta' },
    { value: '#FFA500', label: 'Orange' },
    { value: '#800080', label: 'Purple' },
    { value: '#008000', label: 'Green' },
    { value: '#4682B4', label: 'Steel Blue' },
    { value: '#D2691E', label: 'Chocolate' },
    { value: '#DC143C', label: 'Crimson' },
    { value: '#FFC0CB', label: 'Pink' },
];
// Define zoom constants
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 10.0;
const ZOOM_STEP = 0.1;
const DEFAULT_CELL_SIZE = 50; // Define a base cell size in pixels
// Storage key prefix
const STORAGE_PREFIX = 'levelEditorSettings_'; // Restore constant
// Colors for scenario overlay options
const SCENARIO_OPTION_COLORS = {
    showAbility: '#9C27B0', // Purple - will be dynamically shown based on actual ability
    showCost: '#2196F3', // Blue
    showDifficulty: '#FF9500', // Orange  
    showReward: '#4CAF50', // Green
    showPenalty: '#F44336' // Red
};
// Per-mode stamp amount (different scales: Knowledge ~50–250, energy ~30–50, difficulty
// on the game scale ~120–400, centred on the ~200 default).
export const DEFAULT_STAMP_AMOUNTS = { reward: 50, cost: 40, difficulty: DEFAULT_TRIAL_DIFFICULTY };
// A little rubber-stamp cursor shown over the map while the Balance stamp is active.
const BALANCE_STAMP_CURSOR = (() => {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><g fill='#ffe589' stroke='#0c1f1a' stroke-width='1.6' stroke-linejoin='round'><path d='M11 4h10a3 3 0 0 1 3 3v1.5a3 3 0 0 1-3 3h-2.2l.9 5.5H12.3l.9-5.5H11a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z'/><rect x='6' y='23' width='20' height='4.2' rx='1.6'/></g></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 16 27, crosshair`;
})();
// LevelEditor is now focused on CONTENT editing for a specific level
export function LevelEditor({ levelId, onBack, onReady, leaving }) {
    var _a, _b, _c, _d, _f, _g, _h, _j, _k, _l, _m, _o;
    // Scope the full-screen scroll lock to while the editor is mounted. Without
    // this the lock (LevelEditor.scss) would apply to the whole document and
    // break scrolling on the manager pages.
    useEffect(() => {
        document.documentElement.classList.add('level-editor-active');
        document.body.classList.add('level-editor-active');
        return () => {
            document.documentElement.classList.remove('level-editor-active');
            document.body.classList.remove('level-editor-active');
        };
    }, []);
    // State for the level data being edited
    const [level, updateLevelState] = useImmer(null);
    // Editing lock by moderation status: Pending = fully locked, Approved = image
    // (map render) only, Draft/Rejected = editable. `readOnly` blocks all content edits.
    const editLock = level ? levelEditLock(level.moderationStatus) : 'editable';
    const readOnly = editLock !== 'editable';
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [selectedNodeCoords, setSelectedNodeCoords] = useState(null);
    // Add state for the data of the node currently being edited
    const [editingNodeData, setEditingNodeData] = useState(null);
    // Overlay-line defaults for a fresh level: black, fully opaque, 2px.
    const [overlayOpacity, setOverlayOpacity] = useState(1);
    const [overlayColor, setOverlayColor] = useState(COLOR_OPTIONS[0].value); // Black
    const [lineWidth, setLineWidth] = useState(2);
    const [mapViewMode, setMapViewMode] = useState(null); // null means normal map view
    const [mapOverlays, setMapOverlays] = useState(new Set());
    const [locksOutcomeType, setLocksOutcomeType] = useState('success');
    const [activeToolbarTab, setActiveToolbarTab] = useState('view');
    const [lastMousePos, setLastMousePos] = useState(null);
    const lastMousePosRef = useRef(null);
    const viewportRef = useRef(null);
    const mapBackgroundLayerRef = useRef(null);
    const mapGridLayerRef = useRef(null);
    const canvasStageRef = useRef(null);
    const [canvasTranslate] = useState({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const dragStartCoordsRef = useRef({ x: 0, y: 0 });
    const dragStartOffsetRef = useRef({ x: 0, y: 0 });
    const prevZoomRef = useRef(zoomLevel);
    const didDragRef = useRef(false);
    const DRAG_THRESHOLD = 5;
    const velocityRef = useRef({ x: 0, y: 0 });
    const lastMoveTimeRef = useRef(0);
    const positionsRef = useRef([]);
    const inertiaFrameIdRef = useRef(null);
    // New state for tracking changes and save status
    const [dirtyNodes, setDirtyNodes] = useState(new Set());
    const [saveStatus, setSaveStatus] = useState(SaveStatus.Saved);
    const [lastSaveTime, setLastSaveTime] = useState(null);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    const savingInProgressRef = useRef(false); // Prevent overlapping save operations
    // New state for data saving
    const [saveData, setSaveData] = useState(null);
    // State for artificially showing the auto-save indicator
    const [showSavingIndicator, setShowSavingIndicator] = useState(false);
    const savingTimerRef = useRef(null);
    // State for the action history (UNDO/REDO)
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [maxHistoryLength] = useState(50); // Maximum number of history steps
    const ignoreNextHistoryUpdateRef = useRef(false);
    // Removed unused state for collapsible sections
    // Add new state for cursor type
    const [cursorType, setCursorType] = useState('default');
    // State for showing the stamp panel
    const [showStampPanel, setShowStampPanel] = useState(false);
    // State for expanding the extra stamp buttons
    const [showStampActions, setShowStampActions] = useState(false);
    // State for resetting the stamp configuration
    const [resetStampConfig, setResetStampConfig] = useState(false);
    // Add ref for storing the current stamp configuration
    const lastStampConfigRef = useRef(null);
    // Scenarios toggle — used to anchor its (portal-free) fixed-positioned dropdown.
    const scenariosToggleRef = useRef(null);
    // ----- Unsaved-changes guards, one per embedded editor -----
    const navigateAfterGuard = useCallback((target) => {
        if (target === BACK_SENTINEL) {
            onBack();
        }
        else {
            setActiveToolbarTab(target);
        }
    }, [onBack]);
    const dialogueGuard = useEditorUnsavedGuard({ tab: 'dialogues', savedEvent: 'dialogueUpdated', activeTab: activeToolbarTab, navigate: navigateAfterGuard });
    const eventGuard = useEditorUnsavedGuard({ tab: 'events', savedEvent: 'eventUpdated', activeTab: activeToolbarTab, navigate: navigateAfterGuard });
    const uxPanelGuard = useEditorUnsavedGuard({ tab: 'ux-panels', savedEvent: 'uxPanelUpdated', activeTab: activeToolbarTab, navigate: navigateAfterGuard });
    const scenarioGuard = useEditorUnsavedGuard({ tab: 'interactive', savedEvent: 'scenarioUpdated', activeTab: activeToolbarTab, navigate: navigateAfterGuard });
    const missionGuard = useEditorUnsavedGuard({ tab: 'missions', savedEvent: 'missionUpdated', activeTab: activeToolbarTab, navigate: navigateAfterGuard });
    const editorGuards = [dialogueGuard, eventGuard, uxPanelGuard, scenarioGuard, missionGuard];
    const [nodeTypes, setNodeTypes] = useState([]);
    const [nodeStatuses, setNodeStatuses] = useState([]);
    // State for tracking blinking cells
    const [highlightedCells, setHighlightedCells] = useState(null);
    // State for scenario display options
    const [scenarioDisplayOptions, setScenarioDisplayOptions] = useState({
        showAbility: false,
        showCost: false,
        showDifficulty: false,
        showReward: false,
        showPenalty: false
    });
    // State for controlling the scenarios dropdown menu
    const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState(false);
    // ── Balance stamp brush (Reward/Cost) ─────────────────────────────────────
    // Configured in the Distribution & balance panel; painted onto trial cells on
    // the map. `balanceStampAbilities` empty = applies to every ability.
    const [balanceStampMode, setBalanceStampMode] = useState(null);
    const [balanceStampAbilities, setBalanceStampAbilities] = useState([]);
    const [balanceStampAmounts, setBalanceStampAmounts] = useState(DEFAULT_STAMP_AMOUNTS);
    // Snapshot of the overlay view taken when the stamp turns on, restored on exit.
    const preStampViewRef = useRef(null);
    const handleTabChange = (tab) => {
        // If any editor with unsaved changes owns the current tab, it opens
        // its confirmation modal and the navigation is deferred.
        if (editorGuards.some(guard => guard.intercept(tab))) {
            return;
        }
        setActiveToolbarTab(tab);
    };
    // Load level data AND initial settings
    useEffect(() => {
        var _a, _b, _c, _d, _f;
        setIsLoading(true);
        setError(null);
        setSelectedNodeCoords(null);
        setEditingNodeData(null); // Reset editing data on level load
        setDirtyNodes(new Set()); // Reset dirty nodes on level load
        setSaveStatus(SaveStatus.Saved); // Reset save status on level load
        // Reset the action history when loading a new level
        setHistory([]);
        setHistoryIndex(-1);
        // Load settings from sessionStorage or use defaults
        const storageKey = `${STORAGE_PREFIX}${levelId}`;
        let savedSettings = null;
        try {
            const savedData = sessionStorage.getItem(storageKey);
            if (savedData) {
                savedSettings = JSON.parse(savedData);
            }
        }
        catch (e) {
            console.error("Failed to load settings from sessionStorage", e);
        }
        setOverlayOpacity((_a = savedSettings === null || savedSettings === void 0 ? void 0 : savedSettings.overlayOpacity) !== null && _a !== void 0 ? _a : 1);
        setOverlayColor((_b = savedSettings === null || savedSettings === void 0 ? void 0 : savedSettings.overlayColor) !== null && _b !== void 0 ? _b : COLOR_OPTIONS[0].value); // Black
        setLineWidth((_c = savedSettings === null || savedSettings === void 0 ? void 0 : savedSettings.lineWidth) !== null && _c !== void 0 ? _c : 2);
        setMapViewMode(null); // Default to normal map view
        // Restore overlay settings. Types is on by default on a fresh open;
        // returning to a level restores the creator's saved choice.
        setMapOverlays(new Set((_d = savedSettings === null || savedSettings === void 0 ? void 0 : savedSettings.mapOverlays) !== null && _d !== void 0 ? _d : ['Types']));
        setZoomLevel((_f = savedSettings === null || savedSettings === void 0 ? void 0 : savedSettings.zoomLevel) !== null && _f !== void 0 ? _f : 1.0);
        // Load level data
        const loadLevelData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield getLevelById(levelId);
                // Initialize nodes if missing from backend data
                if (!data.nodes) {
                    data.nodes = {};
                }
                // Load node images for this level
                try {
                    const nodeImages = yield getNodeImages(levelId);
                    // Update nodes with images
                    nodeImages.forEach(image => {
                        const coordString = image.coord;
                        if (data.nodes[coordString]) {
                            data.nodes[coordString].imageUrl = image.imageUrl;
                        }
                        else {
                            // Create a new node with an image if it does not exist
                            data.nodes[coordString] = Object.assign(Object.assign({}, createDefaultNodeData()), { imageUrl: image.imageUrl });
                        }
                    });
                }
                catch (error) {
                    console.error('Error loading node images:', error);
                }
                updateLevelState(data);
                // Initialize the history with the first state
                setHistory([JSON.parse(JSON.stringify(data))]);
                setHistoryIndex(0);
                // Load node types and then migrate legacy types
                getNodeTypes().then(arr => {
                    const active = arr.filter(t => !t.isArchived);
                    setNodeTypes(active);
                    if (level && active.length > 0) {
                        const nameToId = new Map(active.map(t => [t.name, t.id]));
                        updateLevelState(draft => {
                            if (!(draft === null || draft === void 0 ? void 0 : draft.nodes))
                                return;
                            Object.values(draft.nodes).forEach(node => {
                                if (node.typeId == null && node.type) {
                                    const id = nameToId.get(node.type);
                                    if (id !== undefined) {
                                        node.typeId = id;
                                    }
                                }
                            });
                        });
                    }
                }).catch(e => console.error('load nodeTypes', e));
                // Load node statuses
                getNodeStatuses().then(arr => {
                    const active = arr.filter(s => !s.isArchived);
                    setNodeStatuses(active);
                }).catch(e => console.error('load nodeStatuses', e));
            }
            catch (err) {
                console.error(`Error loading level ${levelId}:`, err);
                setError(`Failed to load level content (ID: ${levelId}). Error: ${err.message}`);
                updateLevelState(null);
            }
            finally {
                setIsLoading(false);
            }
        });
        loadLevelData();
    }, [levelId, updateLevelState]);
    // Re-fetch authoritative level data from the backend (nodes + node images +
    // background) and replace editor state. Unlike the initial load it does NOT
    // merge the localStorage node cache — used after AI generation, where the
    // server is the source of truth.
    // `preserveHistory` keeps the undo stack: the reloaded state lands as a NORMAL
    // history entry (via the tracking effect), so the change it reflects — e.g. an
    // auto-Fix applied server-side — stays undoable like any manual edit. The
    // default hard-resets history, which is right for wholesale rebuilds (open,
    // AI generation) where "before" no longer exists as an editable state.
    const reloadFromBackend = useCallback((opts) => __awaiter(this, void 0, void 0, function* () {
        setIsLoading(true);
        try {
            const data = yield getLevelById(levelId);
            if (!data.nodes)
                data.nodes = {};
            try {
                const nodeImages = yield getNodeImages(levelId);
                nodeImages.forEach(image => {
                    const coordString = image.coord;
                    if (data.nodes[coordString]) {
                        data.nodes[coordString].imageUrl = image.imageUrl;
                    }
                    else {
                        data.nodes[coordString] = Object.assign(Object.assign({}, createDefaultNodeData()), { imageUrl: image.imageUrl });
                    }
                });
            }
            catch (e) {
                console.error('Error reloading node images:', e);
            }
            updateLevelState(data);
            if (!(opts === null || opts === void 0 ? void 0 : opts.preserveHistory)) {
                setHistory([JSON.parse(JSON.stringify(data))]);
                setHistoryIndex(0);
            }
            setDirtyNodes(new Set());
            setSaveStatus(SaveStatus.Saved);
        }
        catch (err) {
            setError(`Failed to reload level: ${err.message}`);
        }
        finally {
            setIsLoading(false);
        }
    }), [levelId, updateLevelState]);
    // Apply the result of an AI generation stage to the editor. Text rewrote every
    // cell server-side → full reload + drop the stale local node cache. Image stages
    // only changed the background → surgically update it, preserving unsaved edits.
    const [verifyReport, setVerifyReport] = useState(null);
    const [verifying, setVerifying] = useState(false);
    // The Play view has two sub-modes: 'preview' (the phone preview) and 'avatar'
    // (manual playtest with a real avatar & the game formulas).
    const [playSubMode, setPlaySubMode] = useState('preview');
    // On-map play preview: PlayMode lifts its live run state here; we feed it to the
    // real MapGrid (board) + PhonePreview (trials). Refs expose move/attempt.
    const [playLive, setPlayLive] = useState(null);
    const [powerAmount, setPowerAmount] = useState('500'); // debug "+power" amount in the play bar
    const playApiRef = useRef(null);
    const inPlayPreview = mapViewMode === 'Play' && playSubMode === 'avatar' && !!(playLive === null || playLive === void 0 ? void 0 : playLive.active);
    const [showChecklist, setShowChecklist] = useState(true);
    // Outcome of the last Fix, shown under the verify footer ("Fixed 3 issues" /
    // "nothing auto-fixable"). Cleared whenever a fresh verify runs.
    const [repairNote, setRepairNote] = useState(null);
    const runVerify = useCallback(() => {
        setVerifying(true);
        setRepairNote(null);
        runVerification(levelId)
            .then(setVerifyReport)
            .catch(() => setVerifyReport(null))
            .finally(() => setVerifying(false));
    }, [levelId]);
    const [repairing, setRepairing] = useState(false);
    const handleRepair = useCallback(() => {
        setRepairing(true);
        runRepair(levelId)
            .then((res) => __awaiter(this, void 0, void 0, function* () {
            setVerifyReport(res.after);
            if (res.applied.length > 0) {
                setRepairNote(`Fixed ${res.applied.length} issue${res.applied.length === 1 ? '' : 's'}. The rest needs hand edits.`);
                // preserveHistory: the fix lands as a normal undoable edit — Ctrl+Z
                // restores the pre-fix state (Save then writes it back to the server).
                yield reloadFromBackend({ preserveHistory: true });
            }
            else {
                // Nothing changed server-side — say so instead of silently reloading.
                setRepairNote('Nothing here is auto-fixable — the remaining findings need hand edits.');
            }
        }))
            .catch(() => { })
            .finally(() => setRepairing(false));
    }, [levelId, reloadFromBackend]);
    // Validation is part of the Guide: keep it fresh whenever the Guide is open.
    useEffect(() => { if (showChecklist && !isLoading)
        runVerify(); }, [showChecklist, isLoading, runVerify]);
    const applyGeneration = useCallback((stage, result) => __awaiter(this, void 0, void 0, function* () {
        if (stage === 'text') {
            yield reloadFromBackend();
            // Close the M1→M2 loop: open the Guide (which hosts validation) and
            // auto-verify the freshly generated story.
            setShowChecklist(true);
            runVerify();
        }
        else if (result === null || result === void 0 ? void 0 : result.backgroundImage) {
            updateLevelState(draft => { if (draft)
                draft.backgroundImage = result.backgroundImage; });
        }
    }), [levelId, reloadFromBackend, updateLevelState, runVerify]);
    // Submit the level for admin review straight from the editor (used by the
    // onboarding checklist). Reflects the new status in editor state on success.
    const [submitting, setSubmitting] = useState(false);
    // Editor onboarding tour (submodule of the Tutorial module). Auto-runs the
    // first time a creator opens the editor; replayable from the toolbar "Tour".
    const [editorTourOpen, setEditorTourOpen] = useState(false);
    const editorTourCheckedRef = useRef(false);
    useEffect(() => {
        if (isLoading || editorTourCheckedRef.current)
            return;
        editorTourCheckedRef.current = true;
        // Gate the auto-start on the per-user DB flag, not localStorage: several
        // testers can share a browser, and a stale browser flag would otherwise
        // suppress the tour for a genuinely new user. localStorage is the fallback
        // only if the onboarding API is unreachable.
        getOnboardingStatus()
            .then(status => { if (!status.seenEditorTutorial)
            setEditorTourOpen(true); })
            .catch(() => {
            let seen = false;
            try {
                seen = localStorage.getItem(EDITOR_TOUR.seenKey) === '1';
            }
            catch ( /* ignore */_a) { /* ignore */ }
            if (!seen)
                setEditorTourOpen(true);
        })
            // The level is rendered and the tour is decided — let a parent handoff
            // dim lift (the editor tour's scrim, if it opened, continues the dim).
            .finally(() => onReady === null || onReady === void 0 ? void 0 : onReady());
    }, [isLoading, onReady]);
    // Editor tour dismissed — remember it server-side so it doesn't auto-open again.
    // "Reset to default" — rebuild an imported level from its source Portal file.
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const handleResetToDefault = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setResetting(true);
        try {
            yield resetLevelToDefault(levelId);
            window.location.reload(); // reload everything (nodes/events/panels/images) from the rebuilt level
        }
        catch (e) {
            console.error('reset to default failed', e);
            setResetting(false);
            setResetConfirmOpen(false);
        }
    }), [levelId]);
    const handleEditorTourClose = useCallback(() => {
        setEditorTourOpen(false);
        markEditorTutorialSeen().catch(() => { });
    }, []);
    const handleSubmitForReview = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!level)
            return;
        setSubmitting(true);
        try {
            const updated = yield submitLevel(level.id);
            updateLevelState(draft => { if (draft)
                draft.moderationStatus = updated.moderationStatus; });
        }
        catch (err) {
            alert(`Failed to submit: ${((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (err === null || err === void 0 ? void 0 : err.message) || 'unknown error'}`);
            // A 422 means the solvability gate rejected it — refresh the checklist so
            // the flagged issues show.
            runVerify();
        }
        finally {
            setSubmitting(false);
        }
    }), [level, updateLevelState, runVerify]);
    // Stage 2: send the finished image set to the moderator. On success the set
    // freezes (the checklist flips to its status card); a 422 lists what's missing.
    const [submittingImages, setSubmittingImages] = useState(false);
    const [imagesError, setImagesError] = useState(null);
    const handleSubmitImages = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!level)
            return;
        setSubmittingImages(true);
        setImagesError(null);
        try {
            const updated = yield submitImages(level.id);
            updateLevelState(draft => { if (draft)
                draft.imagesStatus = updated.imagesStatus; });
        }
        catch (err) {
            setImagesError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Failed to submit the images.');
        }
        finally {
            setSubmittingImages(false);
        }
    }), [level, updateLevelState]);
    // Events feed the checklist and the Balance panel.
    const [levelEvents, setLevelEvents] = useState([]);
    const [showDistribution, setShowDistribution] = useState(false);
    const reloadEvents = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            setLevelEvents(yield getLevelEvents(levelId));
        }
        catch (e) {
            console.error('load events', e);
        }
    }), [levelId]);
    useEffect(() => {
        reloadEvents();
    }, [levelId, reloadEvents]);
    // Enhanced saveAllChanges function
    const saveAllChanges = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (readOnly)
            return; // locked while under review / approved — no content writes
        if (!level || !levelId || savingInProgressRef.current)
            return;
        try {
            savingInProgressRef.current = true;
            setSaveStatus(SaveStatus.Saving);
            // Set the saving indicator
            setShowSavingIndicator(true);
            // Validate the data structure before sending (safe to do before sending)
            if (!level.nodes) {
                console.warn("Level nodes property is missing or undefined. Creating empty object.");
                updateLevelState(draft => {
                    if (draft)
                        draft.nodes = {};
                });
                return; // Abort the current save attempt; the updated data will trigger a new effect
            }
            // Deep copy for sending, but narrow the node map to the cells that ACTUALLY
            // changed. `dirtyNodes` was already being tracked (it drives the status text
            // and decides whether to save at all) — it just never narrowed the payload,
            // so every autosave PATCHed all 121 cells of an 11x11 level one request at a
            // time. One edit now costs one request instead of 121, and an autosave burst
            // can no longer still be in flight, competing for connections, when you leave.
            const levelToSave = JSON.parse(JSON.stringify(level));
            // Instead of calling the API directly in the handler, set a flag for the useEffect
            setSaveData({
                level: levelToSave,
                dirtyNodes: new Set(dirtyNodes),
                timestamp: Date.now()
            });
        }
        catch (error) {
            // General error in the saveAllChanges function
            console.error("Error in saveAllChanges function:", error);
            setSaveStatus(SaveStatus.Error);
            alert(`Error during save operation: ${error instanceof Error ? error.message : 'Unknown error'}`);
            savingInProgressRef.current = false;
            setShowSavingIndicator(false); // Reset the indicator on error
        }
    }), [readOnly, level, levelId, dirtyNodes, updateLevelState]);
    // Separate effect to guarantee a minimum display time for the indicator
    useEffect(() => {
        if (saveStatus === SaveStatus.Saving) {
            // When saving starts, start a timer for the minimum display time
            setShowSavingIndicator(true);
            // Clear the previous timer, if any
            if (savingTimerRef.current) {
                clearTimeout(savingTimerRef.current);
            }
            // Set a new timer
            savingTimerRef.current = setTimeout(() => {
                // When the timer expires, check whether saving has finished
                if (saveStatus !== SaveStatus.Saving) {
                    setShowSavingIndicator(false);
                }
            }, 2000); // At least 2 seconds
        }
        else if (saveStatus === SaveStatus.Saved || saveStatus === SaveStatus.Error) {
            // After saving finishes, start the minimum-time timer
            if (savingTimerRef.current) {
                clearTimeout(savingTimerRef.current);
            }
            savingTimerRef.current = setTimeout(() => {
                setShowSavingIndicator(false);
            }, 2000); // The indicator stays visible for at least 2 seconds
        }
        return () => {
            // Cleanup on unmount
            if (savingTimerRef.current) {
                clearTimeout(savingTimerRef.current);
            }
        };
    }, [saveStatus]);
    // Automatically save changes periodically
    useEffect(() => {
        if (readOnly || !autoSaveEnabled || !levelId || !level || dirtyNodes.size === 0) {
            return;
        }
        const autoSaveInterval = setInterval(() => {
            if (dirtyNodes.size > 0 && !savingInProgressRef.current) {
                saveAllChanges();
            }
        }, 10000); // Auto-save every 10 seconds if there are changes (reduced from 30s)
        return () => clearInterval(autoSaveInterval);
    }, [readOnly, autoSaveEnabled, levelId, level, dirtyNodes, saveAllChanges]);
    // Warn user about unsaved changes when leaving the page
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (dirtyNodes.size > 0) {
                const message = "You have unsaved changes. Are you sure you want to leave?";
                e.returnValue = message;
                return message;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [dirtyNodes]);
    // Save settings to sessionStorage on change
    useEffect(() => {
        if (!isLoading && levelId) {
            const settings = {
                overlayOpacity,
                overlayColor,
                lineWidth,
                mapViewMode,
                mapOverlays: Array.from(mapOverlays),
                zoomLevel,
            };
            const storageKey = `${STORAGE_PREFIX}${levelId}`;
            try {
                sessionStorage.setItem(storageKey, JSON.stringify(settings));
            }
            catch (e) {
                console.error("Failed to save settings to sessionStorage", e);
            }
        }
    }, [overlayOpacity, overlayColor, lineWidth, mapViewMode, mapOverlays, levelId, isLoading]);
    // 🚫 REMOVED the useEffect with prevZoomRef - no longer needed, updated in handleZoomTransform
    useEffect(() => {
        lastMousePosRef.current = lastMousePos;
    }, [lastMousePos]);
    // --- Selection Logic ---
    const internalHandleNodeSelect = useCallback((x, y) => {
        const coords = { x, y };
        const coordString = getCoordinateString(x, y);
        let nodeDataToEdit;
        // Get data from main state or create default, potentially updating main state
        updateLevelState(draft => {
            if (!draft)
                return;
            if (!draft.nodes)
                draft.nodes = {}; // Ensure nodes object exists
            if (coordString in draft.nodes) {
                nodeDataToEdit = draft.nodes[coordString];
            }
            else {
                nodeDataToEdit = createDefaultNodeData();
                draft.nodes[coordString] = nodeDataToEdit; // Add default to main state if new
            }
        });
        // Set the selected coords and the data for the editing form state
        // We need to ensure nodeDataToEdit is assigned. Use a temporary variable.
        let finalNodeData = null;
        if ((level === null || level === void 0 ? void 0 : level.nodes) && coordString in level.nodes) {
            finalNodeData = level.nodes[coordString];
        }
        else {
            finalNodeData = createDefaultNodeData(); // Fallback if state update hasn't happened yet
        }
        setSelectedNodeCoords(coords);
        setEditingNodeData(finalNodeData);
    }, [level, updateLevelState]);
    // Add new function to handle stamp application
    const handleApplyStamp = useCallback((stampConfig, nodeCoords) => {
        if (readOnly)
            return; // locked while under review / approved
        if (!level)
            return;
        // Get coordinate string
        const coordString = getCoordinateString(nodeCoords.x, nodeCoords.y);
        // Mark the node as "dirty"
        setDirtyNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(coordString);
            return newSet;
        });
        // Set the status to "unsaved"
        setSaveStatus(SaveStatus.Unsaved);
        // Update the main level state
        updateLevelState(draft => {
            if (!draft)
                return;
            // Ensure nodes object exists
            if (!draft.nodes)
                draft.nodes = {};
            // Get current node data or create default
            const currentNodeData = draft.nodes[coordString] || createDefaultNodeData();
            // Merge stamp configuration with current node data
            // New data has priority over old data
            draft.nodes[coordString] = Object.assign(Object.assign({}, currentNodeData), stampConfig);
        });
    }, [readOnly, level, updateLevelState, setDirtyNodes, setSaveStatus]);
    // Function to activate stamp mode
    const handleActivateStamp = useCallback(() => {
        // Toggle stamp mode on/off
        const newCursorType = cursorType === 'stamp' ? 'default' : 'stamp';
        setCursorType(newCursorType);
        // If we already have a stamp configuration and a selected node, apply the stamp
        if (newCursorType === 'stamp' && lastStampConfigRef.current && selectedNodeCoords) {
            handleApplyStamp(lastStampConfigRef.current, selectedNodeCoords);
        }
    }, [cursorType, selectedNodeCoords, handleApplyStamp]);
    // Function to handle cursor change
    const handleCursorChange = useCallback((cursor) => {
        setCursorType(cursor);
    }, []);
    // ── Balance stamp brush application ───────────────────────────────────────
    // Ability filter helper: empty selection = every ability matches.
    const balanceAbilityMatches = useCallback((a) => balanceStampAbilities.length === 0 || balanceStampAbilities.includes(a), [balanceStampAbilities]);
    // Paint the configured Reward (Knowledge), Cost (Power) or Difficulty onto a clicked
    // trial cell. Only encounters whose ability matches the filter are touched; a
    // cell with no matching trial is left alone (the click is a no-op there).
    const applyBalanceStamp = useCallback((x, y) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const mode = balanceStampMode;
        if (readOnly || !level || !mode)
            return;
        const coord = getCoordinateString(x, y);
        const node = (_a = level.nodes) === null || _a === void 0 ? void 0 : _a[coord];
        if (!node)
            return;
        const eventIds = new Set(((_b = node.simpleEvents) !== null && _b !== void 0 ? _b : []).map(se => se.eventId));
        if (eventIds.size === 0)
            return;
        const amount = Math.max(0, Math.round(balanceStampAmounts[mode]) || 0);
        const updated = [];
        for (const eid of eventIds) {
            const ev = levelEvents.find(e => e.id === eid);
            if (!ev)
                continue;
            let changed = false;
            const encounters = ev.encounters.map(enc => {
                if (!balanceAbilityMatches(enc.ability))
                    return enc;
                changed = true;
                if (mode === 'reward')
                    return Object.assign(Object.assign({}, enc), { rewardType: PrizeType.Knowledge, rewardAmount: amount });
                if (mode === 'cost')
                    return Object.assign(Object.assign({}, enc), { powerCost: amount });
                return Object.assign(Object.assign({}, enc), { difficulty: amount });
            });
            if (changed)
                updated.push(Object.assign(Object.assign({}, ev), { encounters }));
        }
        if (updated.length === 0)
            return;
        // Optimistic: reflect on the map immediately, then persist each event.
        setLevelEvents(prev => prev.map(e => { var _a; return (_a = updated.find(u => u.id === e.id)) !== null && _a !== void 0 ? _a : e; }));
        try {
            for (const ev of updated)
                yield updateEvent(levelId, ev.id, ev);
        }
        catch (err) {
            alert(`Stamp failed: ${((_d = (_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message) || (err === null || err === void 0 ? void 0 : err.message) || 'unknown error'}`);
            yield reloadEvents();
        }
    }), [readOnly, level, balanceStampMode, balanceStampAmounts, balanceAbilityMatches, levelEvents, levelId, reloadEvents]);
    // Sync the map overlay to the active stamp: show the Reward (or Cost) numbers at
    // full opacity so you can see what you paint; restore the previous view on exit.
    useEffect(() => {
        if (balanceStampMode) {
            if (!preStampViewRef.current) {
                preStampViewRef.current = { opacity: overlayOpacity, overlays: new Set(mapOverlays), scenario: scenarioDisplayOptions };
            }
            setActiveToolbarTab('view');
            setOverlayOpacity(1);
            setMapOverlays(new Set(['Types', 'Scenarios']));
            setScenarioDisplayOptions({
                showAbility: false,
                showCost: balanceStampMode === 'cost',
                showDifficulty: balanceStampMode === 'difficulty',
                showReward: balanceStampMode === 'reward',
                showPenalty: false,
            });
        }
        else if (preStampViewRef.current) {
            const prev = preStampViewRef.current;
            setOverlayOpacity(prev.opacity);
            setMapOverlays(prev.overlays);
            setScenarioDisplayOptions(prev.scenario);
            preStampViewRef.current = null;
        }
        // Intentionally only re-run when the stamp mode itself changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [balanceStampMode]);
    // Update handleNodeSelect to apply the stamp on click
    const handleNodeSelect = useCallback((x, y) => {
        if (didDragRef.current)
            return;
        // The Balance Reward/Cost brush takes over clicks while it is active.
        if (balanceStampMode) {
            void applyBalanceStamp(x, y);
            return;
        }
        // Check whether the legacy node-config stamp mode is active
        if (cursorType === 'stamp') {
            // In stamp mode, select the node and immediately apply the stamp to it
            const coords = { x, y };
            setSelectedNodeCoords(coords);
            // Apply the saved stamp configuration, if any
            if (lastStampConfigRef.current && Object.keys(lastStampConfigRef.current).length > 0) {
                handleApplyStamp(lastStampConfigRef.current, coords);
            }
        }
        else {
            // Normal mode - select the node and open the properties panel
            internalHandleNodeSelect(x, y);
        }
    }, [internalHandleNodeSelect, cursorType, handleApplyStamp, balanceStampMode, applyBalanceStamp]);
    const handleCloseNodePanel = () => {
        setSelectedNodeCoords(null);
        setEditingNodeData(null); // Clear editing data on close
    };
    // Enhanced callback function to update node data
    const handleUpdateNodeData = useCallback((coords, updatedData) => {
        if (readOnly)
            return; // locked while under review / approved
        const coordString = getCoordinateString(coords.x, coords.y);
        let newNodeData = null;
        // Mark the node as "dirty"
        setDirtyNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(coordString);
            return newSet;
        });
        // Set the status to "unsaved"
        setSaveStatus(SaveStatus.Unsaved);
        // Update the main level state
        updateLevelState(currentLevel => {
            if (!(currentLevel === null || currentLevel === void 0 ? void 0 : currentLevel.nodes))
                return currentLevel;
            const currentNodeData = currentLevel.nodes[coordString];
            if (!currentNodeData)
                return currentLevel;
            newNodeData = Object.assign(Object.assign({}, currentNodeData), updatedData);
            const newNodes = Object.assign(Object.assign({}, currentLevel.nodes), { [coordString]: newNodeData });
            return Object.assign(Object.assign({}, currentLevel), { nodes: newNodes });
        });
        // Update the local editing state
        if (newNodeData) {
            setEditingNodeData(newNodeData);
        }
        else {
            setEditingNodeData(prevEditingData => {
                if (!prevEditingData)
                    return null;
                const fallbackNewData = Object.assign(Object.assign({}, prevEditingData), updatedData);
                return fallbackNewData;
            });
        }
    }, [readOnly, updateLevelState]);
    // Auto-distribute was retired: rewards are now painted by hand with the Balance
    // Reward/Cost stamp (see applyBalanceStamp). The planners live on in
    // distributionModel.ts for reference/tests.
    // ⚡ PROFESSIONAL ZOOM BASED ON D3.JS PATTERNS
    // Transform state (like in d3.js) - the SINGLE source of truth for zoom
    const [transform, setTransform] = useState({
        scale: zoomLevel,
        translateX: canvasTranslate.x,
        translateY: canvasTranslate.y
    });
    // Center the map in the viewport when a level is opened. The canvas translate
    // isn't persisted, so every open starts at (0,0) — the map's top-left corner
    // pinned to the viewport's top-left, often leaving it shoved into a corner.
    // Re-center once per level load, using the current zoom (translate baked here,
    // scale baked into the cell size).
    const centeredLevelRef = useRef(null);
    useEffect(() => {
        if (isLoading || !level)
            return;
        if (centeredLevelRef.current === levelId)
            return;
        const viewport = viewportRef.current;
        if (!viewport || viewport.offsetWidth === 0)
            return;
        const cell = DEFAULT_CELL_SIZE * transform.scale;
        const mapW = level.mapWidth * cell;
        const mapH = level.mapHeight * cell;
        setTransform(t => (Object.assign(Object.assign({}, t), { translateX: Math.round((viewport.offsetWidth - mapW) / 2), translateY: Math.round((viewport.offsetHeight - mapH) / 2) })));
        centeredLevelRef.current = levelId;
        // transform.scale is read intentionally without subscribing — we only
        // re-center on a fresh level open, not on every zoom.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, levelId, level === null || level === void 0 ? void 0 : level.mapWidth, level === null || level === void 0 ? void 0 : level.mapHeight]);
    // 🚫 REMOVED the useEffect - it created update loops!
    // ── Play preview: show exactly what the player sees ───────────────────────
    // Entering the on-map play preview snaps the overlay to the game-accurate view
    // (Types at full opacity; Codes/Art editor aids OFF) and restores the previous
    // view on exit, so the run isn't cluttered with editor-only annotations.
    const prePlayViewRef = useRef(null);
    useEffect(() => {
        if (inPlayPreview) {
            if (!prePlayViewRef.current) {
                prePlayViewRef.current = { opacity: overlayOpacity, overlays: new Set(mapOverlays) };
            }
            setOverlayOpacity(1);
            setMapOverlays(new Set(['Types']));
        }
        else if (prePlayViewRef.current) {
            setOverlayOpacity(prePlayViewRef.current.opacity);
            setMapOverlays(prePlayViewRef.current.overlays);
            prePlayViewRef.current = null;
        }
        // Only reacts to the play-mode transition (saved/forced/restored once each).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inPlayPreview]);
    // Centre the viewport on the avatar's Start cell once, when the run begins, so
    // the player doesn't have to hunt for it on a large map. Panned SMOOTHLY (a brief
    // CSS transition) instead of snapping, so the editor canvas doesn't jerk.
    // NOTE: the editor canvas intentionally does NOT auto-center when Play starts —
    // it stays exactly where the author left it. (Previously it re-centered on the
    // avatar's start cell; removed per design — the map must not move on Play.)
    // Zoom handler (similar to d3.zoom.on("zoom", zoomed))
    const handleZoomTransform = useCallback((newTransform) => {
        // ⚡ ONLY ONE UPDATE - no loops!
        setTransform(newTransform);
        // Update legacy refs for compatibility
        prevZoomRef.current = newTransform.scale;
    }, []);
    // Professional D3.js-style wheel zoom handler
    const handleWheelZoom = useCallback((e) => {
        // Only enable mouse wheel zoom in Map mode
        if (activeToolbarTab !== 'view')
            return;
        // Check if we're hovering over a modal or interactive element
        const target = e.target;
        if (target.closest('.floating-panel') ||
            target.closest('.allow-native-interaction') ||
            target.closest('.modal') ||
            target.closest('.dropdown')) {
            return; // Allow native scroll on modal elements
        }
        // CRITICAL: Prevent both default behavior AND event bubbling
        e.preventDefault();
        e.stopPropagation();
        // Get mouse coordinates relative to viewport 
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const rect = viewport.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        // Calculate zoom factor (like d3.zoom)
        const scaleFactor = Math.pow(2, -e.deltaY * 0.002);
        const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, transform.scale * scaleFactor));
        // Calculate new translate to keep mouse position fixed (d3.js style)
        const newTranslateX = mouseX - (mouseX - transform.translateX) * (newScale / transform.scale);
        const newTranslateY = mouseY - (mouseY - transform.translateY) * (newScale / transform.scale);
        // Apply transform
        handleZoomTransform({
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        });
    }, [activeToolbarTab, transform, handleZoomTransform]);
    // ⚡ PROFESSIONAL ZOOM METHODS
    const handleZoomIn = useCallback(() => {
        const newScale = Math.min(MAX_ZOOM, transform.scale + ZOOM_STEP);
        // Zoom to viewport center (like d3.js when no anchor point)
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const centerX = viewport.offsetWidth / 2;
        const centerY = viewport.offsetHeight / 2;
        const newTranslateX = centerX - (centerX - transform.translateX) * (newScale / transform.scale);
        const newTranslateY = centerY - (centerY - transform.translateY) * (newScale / transform.scale);
        handleZoomTransform({
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        });
    }, [transform, handleZoomTransform]);
    const handleZoomOut = useCallback(() => {
        const newScale = Math.max(MIN_ZOOM, transform.scale - ZOOM_STEP);
        // Zoom to viewport center  
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const centerX = viewport.offsetWidth / 2;
        const centerY = viewport.offsetHeight / 2;
        const newTranslateX = centerX - (centerX - transform.translateX) * (newScale / transform.scale);
        const newTranslateY = centerY - (centerY - transform.translateY) * (newScale / transform.scale);
        handleZoomTransform({
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        });
    }, [transform, handleZoomTransform]);
    const handleZoomSliderChange = useCallback((newValue) => {
        const newScale = MIN_ZOOM + (newValue / 1000) * (MAX_ZOOM - MIN_ZOOM);
        const clampedScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newScale));
        // Zoom to viewport center
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const centerX = viewport.offsetWidth / 2;
        const centerY = viewport.offsetHeight / 2;
        const newTranslateX = centerX - (centerX - transform.translateX) * (clampedScale / transform.scale);
        const newTranslateY = centerY - (centerY - transform.translateY) * (clampedScale / transform.scale);
        handleZoomTransform({
            scale: clampedScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        });
    }, [transform, handleZoomTransform]);
    // --- Inertia --- 
    const cancelInertiaScroll = useCallback(() => {
        if (inertiaFrameIdRef.current !== null) {
            cancelAnimationFrame(inertiaFrameIdRef.current);
            inertiaFrameIdRef.current = null;
        }
        velocityRef.current = { x: 0, y: 0 }; // Reset velocity
    }, []);
    const startInertiaScroll = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || (Math.abs(velocityRef.current.x) < 0.1 && Math.abs(velocityRef.current.y) < 0.1)) {
            velocityRef.current = { x: 0, y: 0 };
            return; // No significant velocity
        }
        cancelInertiaScroll();
        let { x: vx, y: vy } = velocityRef.current;
        const friction = 0.99; // Even less friction
        let lastFrameTime = performance.now();
        const animationStep = (currentTime) => {
            const deltaTime = currentTime - lastFrameTime;
            lastFrameTime = currentTime;
            const moveX = vx * deltaTime;
            const moveY = vy * deltaTime;
            // ⚡ Update transform instead of old canvasTranslate
            const newTransform = {
                scale: transform.scale,
                translateX: transform.translateX + moveX,
                translateY: transform.translateY + moveY
            };
            handleZoomTransform(newTransform);
            vx *= Math.pow(friction, deltaTime / 16.67);
            vy *= Math.pow(friction, deltaTime / 16.67);
            if (Math.abs(vx) > 0.001 || Math.abs(vy) > 0.001) {
                velocityRef.current = { x: vx, y: vy };
                inertiaFrameIdRef.current = requestAnimationFrame(animationStep);
            }
            else {
                inertiaFrameIdRef.current = null;
                velocityRef.current = { x: 0, y: 0 }; // Final reset
            }
        };
        inertiaFrameIdRef.current = requestAnimationFrame(animationStep);
    }, [cancelInertiaScroll, transform, handleZoomTransform]);
    // --- Mouse Handlers ---
    const handleMouseDown = (e) => {
        // If right button, don't do anything
        if (e.button !== 0)
            return;
        // Don't start drag if click happened on a panel (allow native interaction)
        if (e.target.closest('.allow-native-interaction')) {
            return;
        }
        // If stamp mode is active, don't start dragging
        if (cursorType === 'stamp') {
            return;
        }
        // Default drag behavior
        e.preventDefault();
        setCursorType('grabbing');
        const clientX = e.clientX;
        const clientY = e.clientY;
        didDragRef.current = false;
        dragStartCoordsRef.current = { x: clientX, y: clientY };
        dragStartOffsetRef.current = {
            x: transform.translateX,
            y: transform.translateY
        };
        isDraggingRef.current = true;
        // The browser must not start selecting map labels when the cursor leaves
        // the viewport mid-drag (mousedown's preventDefault stops covering it).
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        setLastMousePos({ x: clientX, y: clientY });
        lastMoveTimeRef.current = Date.now();
        positionsRef.current = [{ x: clientX, y: clientY, time: lastMoveTimeRef.current }];
        // Cancel any active inertia
        cancelInertiaScroll();
    };
    const handleMouseMoveWrapper = (e) => {
        if (cursorType === 'stamp') {
            // For stamp mode, update last mouse position but don't start dragging
            setLastMousePos({ x: e.clientX, y: e.clientY });
            return;
        }
        // Panning is handled by the DOCUMENT-level listener below (so the drag
        // survives the cursor leaving the viewport); here only track the hover
        // position for zoom-at-cursor.
        if (!isDraggingRef.current) {
            setLastMousePos({ x: e.clientX, y: e.clientY });
        }
    };
    // Panning listens on the DOCUMENT, not the viewport div: with element-scoped
    // listeners the drag froze (and the browser started selecting text) the moment
    // the cursor crossed the viewport edge. Moves are coalesced through rAF — one
    // transform per frame instead of one React render per 120 Hz mouse event,
    // which is what made dragging jerky.
    const panPointRef = useRef(null);
    const panRafRef = useRef(null);
    const lastPanTsRef = useRef(0);
    useEffect(() => {
        const applyPan = () => {
            panRafRef.current = null;
            lastPanTsRef.current = performance.now();
            const p = panPointRef.current;
            if (!p || !isDraggingRef.current)
                return;
            const dx = p.x - dragStartCoordsRef.current.x;
            const dy = p.y - dragStartCoordsRef.current.y;
            if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
                didDragRef.current = true;
            }
            handleZoomTransform({
                scale: prevZoomRef.current, // always-current scale (kept by handleZoomTransform)
                translateX: dragStartOffsetRef.current.x + dx,
                translateY: dragStartOffsetRef.current.y + dy,
            });
        };
        const onDocMouseMove = (e) => {
            if (!isDraggingRef.current)
                return;
            panPointRef.current = { x: e.clientX, y: e.clientY };
            // Leading edge applies DIRECTLY (never depends on rAF firing — rAF is
            // frozen in hidden tabs); anything faster than a frame is coalesced
            // into one trailing rAF.
            if (performance.now() - lastPanTsRef.current >= 15) {
                applyPan();
            }
            else if (panRafRef.current === null) {
                panRafRef.current = requestAnimationFrame(applyPan);
            }
            // Velocity tracking for the release inertia (unchanged behaviour).
            const now = Date.now();
            const timeDelta = now - lastMoveTimeRef.current;
            if (timeDelta > 16) { // Roughly 60fps
                const lastPosition = positionsRef.current[positionsRef.current.length - 1];
                const speedX = (e.clientX - lastPosition.x) / timeDelta; // px/ms
                const speedY = (e.clientY - lastPosition.y) / timeDelta; // px/ms
                velocityRef.current = {
                    x: speedX * 0.9 + velocityRef.current.x * 0.1,
                    y: speedY * 0.9 + velocityRef.current.y * 0.1,
                };
                positionsRef.current.push({ x: e.clientX, y: e.clientY, time: now });
                if (positionsRef.current.length > 5)
                    positionsRef.current.shift();
                lastMoveTimeRef.current = now;
            }
        };
        document.addEventListener('mousemove', onDocMouseMove);
        return () => {
            document.removeEventListener('mousemove', onDocMouseMove);
            if (panRafRef.current !== null)
                cancelAnimationFrame(panRafRef.current);
        };
    }, [handleZoomTransform]);
    const stopDragging = () => {
        if (isDraggingRef.current) {
            isDraggingRef.current = false;
            // Re-enable text selection disabled for the duration of the pan.
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            // Only start inertia if we actually did a real drag
            if (didDragRef.current && positionsRef.current.length >= 2) {
                // Calculate final velocity from positions
                const oldest = positionsRef.current[0];
                const newest = positionsRef.current[positionsRef.current.length - 1];
                const totalDx = newest.x - oldest.x;
                const totalDy = newest.y - oldest.y;
                const totalTime = newest.time - oldest.time;
                if (totalTime > 0) {
                    velocityRef.current = {
                        x: totalDx / totalTime, // px/ms
                        y: totalDy / totalTime // px/ms
                    };
                    // Apply initial multiplier
                    velocityRef.current.x *= 400; // Higher values = stronger inertia
                    velocityRef.current.y *= 400;
                    // Start inertia animation if significant velocity
                    if (Math.abs(velocityRef.current.x) > 0.1 || Math.abs(velocityRef.current.y) > 0.1) {
                        startInertiaScroll();
                    }
                }
            }
            setCursorType('grab');
        }
    };
    // Global mouseup calls stopDragging
    useEffect(() => {
        const handleMouseUpGlobal = (_e) => {
            stopDragging();
        };
        document.addEventListener('mouseup', handleMouseUpGlobal);
        return () => {
            document.removeEventListener('mouseup', handleMouseUpGlobal);
        };
    }, [stopDragging]);
    // View control handlers
    const handleOverlayOpacityChange = (newValue) => {
        setOverlayOpacity(newValue / 100);
    };
    const handleColorChange = (selectedOptionValue) => {
        if (typeof selectedOptionValue === 'string') {
            setOverlayColor(selectedOptionValue);
        }
    };
    const handleLineWidthChange = (newValue) => {
        setLineWidth(newValue);
    };
    // Handler for changing the map view mode (now only Play mode)
    const handlePlayModeToggle = () => {
        const next = mapViewMode === 'Play' ? null : 'Play';
        setMapViewMode(next);
        // Play goes straight to Avatar mode — the standalone phone "Preview" sub-mode is retired.
        if (next === 'Play')
            setPlaySubMode('avatar');
    };
    // Handler for toggling overlay modes (checkbox behavior)
    const handleOverlayToggle = (overlayMode) => {
        if (overlayMode === 'Scenarios') {
            // For Scenarios, toggle the dropdown instead of turning it on/off directly
            setScenarioDropdownOpen(prev => !prev);
            return;
        }
        setMapOverlays(prev => {
            const newSet = new Set(prev);
            if (newSet.has(overlayMode)) {
                newSet.delete(overlayMode);
            }
            else {
                newSet.add(overlayMode);
            }
            return newSet;
        });
    };
    // Handler for toggling scenario display options
    const handleScenarioOptionToggle = (option) => {
        setScenarioDisplayOptions(prev => (Object.assign(Object.assign({}, prev), { [option]: !prev[option] })));
        // Enable the Scenarios overlay if at least one option is active
        const newOptions = Object.assign(Object.assign({}, scenarioDisplayOptions), { [option]: !scenarioDisplayOptions[option] });
        const hasAnyOption = newOptions.showAbility || newOptions.showCost || newOptions.showDifficulty || newOptions.showReward || newOptions.showPenalty;
        setMapOverlays(prev => {
            const newSet = new Set(prev);
            if (hasAnyOption) {
                newSet.add('Scenarios');
            }
            else {
                newSet.delete('Scenarios');
            }
            return newSet;
        });
    };
    // Removed getMapGridViewMode - now using direct overlay props
    // ⚡ Calculate slider value from TRANSFORM state (no old zoomLevel)
    const zoomSliderValue = Math.round(((transform.scale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 1000);
    // Use a useEffect to run the asynchronous request
    useEffect(() => {
        // If there is no data to save, exit
        if (!saveData || !levelId)
            return;
        const saveLevelData = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                // Send only the cells that ACTUALLY changed. `dirtyNodes` was already
                // tracked (it drives the status text and decides whether to save at all)
                // but never narrowed the payload, so each autosave PATCHed all 121 cells
                // of an 11x11 level, one sequential request each. One edit now costs one
                // request, so a burst can't still be in flight competing for the
                // browser's connections when you navigate away.
                // NOTE: keep saveData.level.nodes intact — the local state below is
                // rebuilt from it, and narrowing that object would wipe every cell that
                // wasn't dirty out of the in-memory level.
                const dirty = saveData.dirtyNodes;
                const allNodes = (_a = saveData.level.nodes) !== null && _a !== void 0 ? _a : {};
                const nodesToSend = dirty && dirty.size > 0
                    ? Object.fromEntries([...dirty].filter(c => allNodes[c] !== undefined).map(c => [c, allNodes[c]]))
                    : allNodes;
                const savedLevel = yield updateLevel(levelId, Object.assign(Object.assign({}, saveData.level), { nodes: nodesToSend }));
                // savedLevel does not contain nodes, since they are updated separately
                // We need to build a full level object to update the local state
                const updatedLevel = Object.assign(Object.assign({}, savedLevel), { nodes: saveData.level.nodes || {} });
                // Update the level state
                updateLevelState(updatedLevel);
                // If all nodes were saved successfully, update the status
                setSaveStatus(SaveStatus.Saved);
                setLastSaveTime(new Date());
                setDirtyNodes(new Set()); // Clear the list of modified nodes
            }
            catch (error) {
                // Detailed logging of the API error
                console.error("API Error during save:", error);
                if (error.response) {
                    console.error("Response data:", error.response.data);
                    console.error("Response status:", error.response.status);
                }
                else if (error.request) {
                    console.error("No response received. Request:", error.request);
                }
                else {
                    console.error("Error message:", error.message);
                }
                const errorMessage = ((_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) || error.message || "Unknown error";
                setSaveStatus(SaveStatus.Error);
                alert(`Failed to save changes: ${errorMessage}. Check console for details.`);
            }
            finally {
                savingInProgressRef.current = false;
                // Clear the save data
                setSaveData(null);
            }
        });
        // Run the asynchronous function
        saveLevelData();
    }, [saveData, levelId, updateLevelState]);
    // Back handler that accounts for unsaved changes in any embedded editor
    const handleBackWithSave = useCallback(() => {
        if (editorGuards.some(guard => guard.intercept(BACK_SENTINEL))) {
            return;
        }
        onBack();
        // editorGuards is rebuilt each render; the callback only needs the latest one
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeToolbarTab, dialogueGuard.hasUnsaved, eventGuard.hasUnsaved, uxPanelGuard.hasUnsaved, scenarioGuard.hasUnsaved, missionGuard.hasUnsaved, onBack]);
    // Toggle auto-save
    const toggleAutoSave = useCallback(() => {
        setAutoSaveEnabled(prev => !prev);
    }, []);
    // Add the current state to the history
    const addToHistory = useCallback((currentLevel) => {
        if (!currentLevel || ignoreNextHistoryUpdateRef.current) {
            ignoreNextHistoryUpdateRef.current = false;
            return;
        }
        setHistory(prevHistory => {
            // Create a deep copy of the current state
            const stateCopy = JSON.parse(JSON.stringify(currentLevel));
            // Drop states after the current index (if the user did an undo and then a new action)
            const newHistory = prevHistory.slice(0, historyIndex + 1);
            // Add the new state
            newHistory.push(stateCopy);
            // Limit the history length
            if (newHistory.length > maxHistoryLength) {
                newHistory.shift();
            }
            return newHistory;
        });
        // Update the history index
        setHistoryIndex(prev => {
            const newIndex = Math.min(prev + 1, maxHistoryLength - 1);
            return newIndex;
        });
    }, [historyIndex, maxHistoryLength]);
    // Watch for level changes and add them to the history
    useEffect(() => {
        if (level && historyIndex >= 0 && !ignoreNextHistoryUpdateRef.current) {
            // Check whether the current state differs from the last one in the history
            const lastHistoryState = history[historyIndex];
            if (lastHistoryState && JSON.stringify(lastHistoryState) !== JSON.stringify(level)) {
                addToHistory(level);
            }
        }
    }, [level, addToHistory, history, historyIndex]);
    // Function to undo the last action (UNDO)
    const handleUndo = useCallback(() => {
        if (readOnly)
            return; // locked while under review / approved
        if (historyIndex > 0) {
            // Set the ignore flag so this change is not added to the history
            ignoreNextHistoryUpdateRef.current = true;
            // Move to the previous state
            const prevState = history[historyIndex - 1];
            if (prevState) {
                updateLevelState(JSON.parse(JSON.stringify(prevState)));
                setHistoryIndex(historyIndex - 1);
                // Mark changed nodes as "dirty" for the subsequent save
                if (prevState.nodes) {
                    const dirtyNodeKeys = Object.keys(prevState.nodes);
                    setDirtyNodes(new Set(dirtyNodeKeys));
                    setSaveStatus(SaveStatus.Unsaved);
                }
            }
        }
    }, [readOnly, historyIndex, history, updateLevelState]);
    // Function to redo the last undone action (REDO)
    const handleRedo = useCallback(() => {
        if (readOnly)
            return; // locked while under review / approved
        if (historyIndex < history.length - 1) {
            // Set the ignore flag
            ignoreNextHistoryUpdateRef.current = true;
            // Move to the next state
            const nextState = history[historyIndex + 1];
            if (nextState) {
                updateLevelState(JSON.parse(JSON.stringify(nextState)));
                setHistoryIndex(historyIndex + 1);
                // Mark changed nodes as "dirty" for the subsequent save
                if (nextState.nodes) {
                    const dirtyNodeKeys = Object.keys(nextState.nodes);
                    setDirtyNodes(new Set(dirtyNodeKeys));
                    setSaveStatus(SaveStatus.Unsaved);
                }
            }
        }
    }, [readOnly, historyIndex, history, updateLevelState]);
    // Keyboard shortcut handler for UNDO/REDO
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Make sure the focus is not in a text field or another input element
            const activeElement = document.activeElement;
            const isInputActive = activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement ||
                activeElement instanceof HTMLSelectElement;
            if (isInputActive)
                return;
            // Ctrl+Z for UNDO
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
            // Ctrl+Y or Ctrl+Shift+Z for REDO
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                handleRedo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo]);
    // Handle click outside for color picker dropdown and scenarios dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            // Handle color picker dropdown
            if (!target.closest('.color-picker-dropdown') && !target.closest('.color-picker-trigger')) {
                const dropdown = document.getElementById('overlay-color-dropdown');
                if (dropdown) {
                    dropdown.style.display = 'none';
                }
            }
            // Handle scenarios dropdown
            if (!target.closest('.scenarios-dropdown-container')) {
                setScenarioDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);
    // Update editor-viewport to use cursor type
    const getViewportCursor = () => {
        // The Balance stamp brush shows a stamp cursor so it's clear clicks paint cells.
        if (balanceStampMode)
            return BALANCE_STAMP_CURSOR;
        switch (cursorType) {
            case 'grab':
                return 'grab';
            case 'grabbing':
                return 'grabbing';
            case 'stamp':
                return 'copy';
            default:
                return 'default';
        }
    };
    // Function to toggle the visibility of the stamp panel along with the extra buttons
    const toggleStampPanel = useCallback(() => {
        if (showStampPanel) {
            // If the panel is open, close it
            setShowStampPanel(false);
            // Reset the cursor mode when closing the panel, but only if it is in stamp mode
            if (cursorType === 'stamp') {
                setCursorType('default');
            }
            // Hide the extra buttons when closing the panel
            setShowStampActions(false);
        }
        else {
            // If the panel is closed, open it and show the extra buttons
            setShowStampPanel(true);
            setShowStampActions(true);
        }
    }, [showStampPanel, cursorType]);
    // Function to reset the stamp configuration without changing the panel position
    const handleResetStamp = useCallback(() => {
        // Set the reset flag that will be passed to StampTool
        setResetStampConfig(true);
        // Reset the flag after a short delay
        setTimeout(() => {
            setResetStampConfig(false);
        }, 100);
    }, []);
    // Callback for handling cell highlighting from PhonePreview
    const handleHighlightCells = useCallback((cells, type, isPlaying) => {
        const timestamp = Date.now();
        setHighlightedCells({
            cells,
            type,
            timestamp
        });
        // Remove the highlight after 3 seconds, but only if playback is active
        // While paused, the highlight stays until playback resumes or the step changes
        if (isPlaying) {
            setTimeout(() => {
                setHighlightedCells(prev => prev && prev.timestamp === timestamp ? null : prev);
            }, 3000);
        }
    }, []);
    // In play preview, blink the cells a resolved trial just unlocked / locked on
    // the real map. Keyed on the last step's identity (stable within a run).
    const lastPlayStep = (_a = playLive === null || playLive === void 0 ? void 0 : playLive.lastStep) !== null && _a !== void 0 ? _a : null;
    useEffect(() => {
        if (!lastPlayStep)
            return;
        if (lastPlayStep.unlocked && lastPlayStep.unlocked.length > 0)
            handleHighlightCells(lastPlayStep.unlocked, 'unlock', true);
        else if (lastPlayStep.locked && lastPlayStep.locked.length > 0)
            handleHighlightCells(lastPlayStep.locked, 'lock', true);
    }, [lastPlayStep, handleHighlightCells]);
    // Callback for highlighting cells when hovering over Lock/Unlock actions in Node Properties
    const handleHighlightNodeAction = useCallback((cells, type) => {
        if (type === null || cells.length === 0) {
            setHighlightedCells(null);
        }
        else {
            setHighlightedCells({
                cells,
                type,
                timestamp: Date.now()
            });
        }
    }, []);
    // --- Render --- 
    // Loading / Error checks first
    if (isLoading) {
        return _jsx("div", { className: "loading-fullscreen", children: "Loading Level Editor..." });
    }
    if (error) {
        return (_jsxs("div", { className: "error-fullscreen", children: [_jsx("p", { children: error }), _jsx("button", { onClick: handleBackWithSave, children: "Back to Level Manager" })] }));
    }
    if (!level) {
        return (_jsxs("div", { className: "error-fullscreen", children: [_jsx("p", { children: "Level data is unavailable." }), _jsx("button", { onClick: handleBackWithSave, children: "Back to Level Manager" })] }));
    }
    // Now we know level is not null
    // Removed calculation of selectedNodeData here - use editingNodeData state instead
    /*
    if (!level.nodes) {
      level.nodes = {};
    }
    const selectedNodeData = selectedNodeCoords && level.nodes
      ? level.nodes[getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y)] ?? createDefaultNodeData()
      : createDefaultNodeData();
    */
    // Truncate level name for display
    const truncatedLevelName = level.name.length > 10
        ? `${level.name.substring(0, 10)}...`
        : level.name;
    // Construct background image URL (absolute URLs — e.g. imported game CDN
    // art — pass through; only relative /uploads paths get the backend prefix).
    const backgroundImageUrl = level.backgroundImage
        ? (level.backgroundImage.startsWith('http') ? level.backgroundImage : `${backendUrl}${level.backgroundImage}`)
        : undefined;
    // ⚡ Calculate scaled map dimensions from TRANSFORM (no more old states!)
    const scaledCellSize = DEFAULT_CELL_SIZE * transform.scale;
    const mapPixelWidth = level.mapWidth * scaledCellSize;
    const mapPixelHeight = level.mapHeight * scaledCellSize;
    const clampedWidth = mapPixelWidth;
    const clampedHeight = mapPixelHeight;
    const canvasStageStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${clampedWidth}px`,
        height: `${clampedHeight}px`,
        transform: `translate(${transform.translateX}px, ${transform.translateY}px)`,
    };
    // The background is NOT stretched across the authored grid — the game lays the
    // whole image over a PADDED grid and leaves the outer MAP_PADDING_CELLS rings
    // decorative (see the MAP GEOMETRY block in contracts.ts). So the image spans
    // MAP_PADDED_CELLS cells and starts MAP_PADDING_CELLS cells above/left of A1,
    // which is exactly where the player will see it.
    const paddingPx = MAP_PADDING_CELLS * scaledCellSize;
    const backgroundSpanPx = MAP_PADDED_CELLS * scaledCellSize;
    const mapBackgroundLayerStyle = {
        position: 'absolute',
        top: `${-paddingPx}px`,
        left: `${-paddingPx}px`,
        width: `${backgroundSpanPx}px`,
        height: `${backgroundSpanPx}px`,
        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
        backgroundSize: '100% 100%',
        backgroundPosition: 'top left',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
    };
    const mapGridLayerStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${clampedWidth}px`,
        height: `${clampedHeight}px`,
        pointerEvents: 'none',
    };
    // Render the save status indicator
    const renderSaveStatus = () => {
        let statusText = "";
        let statusIcon = null;
        let statusClass = "";
        switch (saveStatus) {
            case SaveStatus.Saved:
                statusText = lastSaveTime
                    ? `Saved at ${lastSaveTime.toLocaleTimeString()}`
                    : "All changes saved";
                statusIcon = _jsx(FaCheck, {});
                statusClass = "status-saved";
                break;
            case SaveStatus.Saving:
                statusText = "Saving...";
                statusIcon = _jsx(FaSync, { className: "icon-spin" });
                statusClass = "status-saving";
                break;
            case SaveStatus.Unsaved:
                statusText = `${dirtyNodes.size} unsaved ${dirtyNodes.size !== 1 ? 'changes' : 'change'}`;
                statusIcon = _jsx(FaExclamationTriangle, {});
                statusClass = "status-unsaved";
                break;
            case SaveStatus.Error:
                statusText = "Save failed";
                statusIcon = _jsx(FaExclamationTriangle, {});
                statusClass = "status-error";
                break;
        }
        return (_jsxs("div", { className: `save-status ${statusClass}`, children: [_jsx("span", { className: "status-icon", children: statusIcon }), _jsx("span", { className: "status-text", children: statusText }), saveStatus !== SaveStatus.Saved && saveStatus !== SaveStatus.Saving && (_jsxs("button", { onClick: saveAllChanges, className: "save-button", title: "Save all changes", children: [_jsx(FaSave, {}), " Save"] })), _jsxs("button", { onClick: toggleAutoSave, className: `autosave-toggle ${autoSaveEnabled ? 'active' : 'inactive'} ${showSavingIndicator ? 'saving' : ''}`, title: autoSaveEnabled ?
                        (showSavingIndicator ? "Auto-saving..." : "Auto-save enabled") :
                        "Enable auto-save", children: ["Auto: ", autoSaveEnabled ? "ON" : "OFF"] })] }));
    };
    // Compute whether at least one scenario option is enabled
    const hasAnyScenarioOption = Object.values(scenarioDisplayOptions).some(Boolean);
    // The level's current start point (the cell with the "Start" node type), so the
    // AI modal pre-selects it instead of resetting/overwriting the chosen cell.
    const editorStartPoint = (() => {
        var _a, _b;
        const startTypeId = (_a = nodeTypes.find(t => t.name === 'Start')) === null || _a === void 0 ? void 0 : _a.id;
        if (!startTypeId)
            return null;
        for (const [coord, node] of Object.entries((_b = level.nodes) !== null && _b !== void 0 ? _b : {})) {
            if ((node === null || node === void 0 ? void 0 : node.typeId) === startTypeId) {
                const p = parseCoord(coord);
                if (p)
                    return { x: p.x, y: p.y };
            }
        }
        return null;
    })();
    // Main editor layout
    return (_jsxs("div", { className: `level-editor-layout ${leaving ? 'editor-leaving' : 'editor-enter'} ${selectedNodeCoords && activeToolbarTab === 'view' ? 'side-panel-open' : ''}`, children: [readOnly && (_jsxs("div", { className: `edit-lock-banner ${editLock === 'image-only' ? 'image-only' : 'locked'}`, children: [_jsx(FaLock, { style: { marginRight: 6, verticalAlign: '-1px' } }), editLock === 'image-only'
                        ? 'This level is approved and locked. You can only (re)generate the background map image in the Art tab.'
                        : 'This level is under review. Editing is locked until moderation completes.'] })), _jsxs("div", { className: "editor-toolbar", children: [_jsxs("div", { className: "toolbar-header", children: [_jsx("button", { onClick: handleBackWithSave, className: "toolbar-button", title: "Save and Back to Manager", children: "\u2190" }), _jsx("span", { className: "toolbar-title truncated", title: level.name, children: truncatedLevelName }), _jsxs("div", { className: "toolbar-tabs", children: [_jsx("button", { className: `tab-button ${activeToolbarTab === 'view' ? 'active' : ''}`, onClick: () => handleTabChange('view'), children: "Map" }), _jsx("button", { className: `tab-button ${activeToolbarTab === 'events' ? 'active' : ''}`, onClick: () => handleTabChange('events'), children: "Events" }), _jsx("button", { className: `tab-button ${activeToolbarTab === 'ux-panels' ? 'active' : ''}`, onClick: () => handleTabChange('ux-panels'), children: "UX Panels" }), _jsx("button", { className: `tab-button ${activeToolbarTab === 'art' ? 'active' : ''}`, onClick: () => handleTabChange('art'), children: "Art" })] }), _jsxs("div", { className: "history-buttons", children: [_jsx("button", { onClick: handleUndo, className: "history-button", title: "Undo (Ctrl+Z)", disabled: historyIndex <= 0, children: _jsx(FaUndo, {}) }), _jsx("button", { onClick: handleRedo, className: "history-button", title: "Redo (Ctrl+Y)", disabled: historyIndex >= history.length - 1, children: _jsx(FaRedo, {}) })] }), _jsxs("div", { className: "editor-tools", children: [activeToolbarTab === 'view' && (_jsxs(_Fragment, { children: [_jsxs("button", { onClick: () => setShowChecklist(s => !s), className: `editor-tool-button ${showChecklist ? 'active' : ''}`, title: "Portal checklist", children: [_jsx(FaClipboardCheck, {}), _jsx("span", { children: "Guide" })] }), _jsxs("button", { onClick: () => setShowDistribution(s => !s), className: `editor-tool-button ${showDistribution ? 'active' : ''}`, title: "Distribution & balance", children: [_jsx(FaBalanceScale, {}), _jsx("span", { children: "Balance" })] })] })), _jsx(AIGenerationPanel, { levelId: levelId, levelName: level.name, mapWidth: level.mapWidth, mapHeight: level.mapHeight, initialStart: editorStartPoint, backgroundImage: level.backgroundImage, moderationStatus: level.moderationStatus, isChallengeEntry: level.challengeId != null, onApplied: applyGeneration }), _jsxs("button", { onClick: () => setEditorTourOpen(true), className: "editor-tool-button", title: "Replay the editor tutorial", children: [_jsx(FaGraduationCap, {}), _jsx("span", { children: "Tour" })] }), level.sourcePortalId != null && (_jsxs("button", { onClick: () => setResetConfirmOpen(true), className: "editor-tool-button", title: "Reset the whole level to its imported default (discards all edits)", children: [_jsx(FaUndo, {}), _jsx("span", { children: "Reset" })] }))] }), renderSaveStatus(), " "] }), activeToolbarTab !== 'art' && (_jsxs("div", { className: "toolbar-content", children: [activeToolbarTab === 'view' && (_jsx("div", { className: "view-tab-content", children: _jsxs("div", { className: "unified-controls", children: [_jsxs("div", { className: "control-block overlay-view-block", children: [_jsxs("div", { className: "block-header", children: [_jsx("span", { children: "Overlay & View" }), _jsxs("button", { className: "opacity-toggle", onClick: () => setOverlayOpacity(overlayOpacity > 0 ? 0 : 0.8), title: overlayOpacity > 0 ? "Hide overlay" : "Show overlay", children: [_jsx("span", { className: "opacity-indicator", style: { backgroundColor: overlayColor, opacity: overlayOpacity } }), _jsxs("span", { className: "zoom-value", children: [Math.round(overlayOpacity * 100), "%"] })] })] }), _jsx("div", { className: "block-content", children: _jsxs("div", { className: "view-toggles", children: [_jsx("div", { className: "compact-color-picker", children: _jsx("div", { className: "color-picker-trigger", style: { backgroundColor: overlayColor }, onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        const dropdown = document.getElementById('overlay-color-dropdown');
                                                                        if (dropdown) {
                                                                            if (dropdown.style.display === 'block') {
                                                                                dropdown.style.display = 'none';
                                                                            }
                                                                            else {
                                                                                // Position dropdown relative to trigger button
                                                                                const trigger = e.currentTarget;
                                                                                const rect = trigger.getBoundingClientRect();
                                                                                dropdown.style.top = `${rect.bottom + 8}px`;
                                                                                dropdown.style.left = `${rect.left}px`;
                                                                                dropdown.style.display = 'block';
                                                                            }
                                                                        }
                                                                    }, children: _jsxs("div", { className: "color-picker-dropdown", id: "overlay-color-dropdown", style: { display: 'none' }, onClick: (e) => e.stopPropagation(), children: [_jsx("div", { className: "color-options-grid", children: COLOR_OPTIONS.map((option) => (_jsx("div", { className: `color-option ${overlayColor === option.value ? 'selected' : ''}`, style: { backgroundColor: option.value }, onClick: (e) => {
                                                                                        e.stopPropagation();
                                                                                        handleColorChange(option.value);
                                                                                        const dropdown = document.getElementById('overlay-color-dropdown');
                                                                                        if (dropdown)
                                                                                            dropdown.style.display = 'none';
                                                                                    } }, option.value))) }), _jsxs("div", { className: "opacity-slider-container", children: [_jsxs("label", { children: ["Opacity: ", Math.round(overlayOpacity * 100), "%"] }), _jsx(SliderInput, { min: 0, max: 100, step: 1, value: overlayOpacity * 100, onChange: handleOverlayOpacityChange, showValue: false })] }), _jsxs("div", { className: "line-width-slider-container", children: [_jsxs("label", { children: ["Line Width: ", lineWidth, "px"] }), _jsx(SliderInput, { min: 1, max: 4, step: 1, value: lineWidth, onChange: handleLineWidthChange, showValue: false })] })] }) }) }), _jsx("button", { className: `view-toggle ${mapOverlays.has('Types') ? 'active' : ''}`, onClick: () => handleOverlayToggle('Types'), children: "Types" }), _jsx("button", { className: `view-toggle ${mapOverlays.has('Codes') ? 'active' : ''}`, onClick: () => handleOverlayToggle('Codes'), children: "Codes" }), _jsx("button", { className: `view-toggle ${mapOverlays.has('Art') ? 'active' : ''}`, onClick: () => handleOverlayToggle('Art'), children: "Art" }), _jsxs("div", { className: "scenarios-dropdown-container", children: [_jsxs("button", { ref: scenariosToggleRef, className: `view-toggle scenarios-toggle ${hasAnyScenarioOption ? 'active' : ''}`, onClick: () => handleOverlayToggle('Scenarios'), children: ["Scenarios", _jsx(FaChevronDown, { className: `dropdown-icon ${scenarioDropdownOpen ? 'open' : ''}` })] }), scenarioDropdownOpen && (_jsxs("div", { className: "scenarios-dropdown", style: (() => {
                                                                            var _a;
                                                                            const r = (_a = scenariosToggleRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                                                            return r ? { position: 'fixed', top: r.bottom + 8, left: r.left, margin: 0 } : undefined;
                                                                        })(), children: [_jsx("div", { className: "dropdown-item", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: scenarioDisplayOptions.showAbility, onChange: () => handleScenarioOptionToggle('showAbility') }), _jsx("span", { className: "colored-label", style: { backgroundColor: SCENARIO_OPTION_COLORS.showAbility }, children: "Ability" })] }) }), _jsx("div", { className: "dropdown-item", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: scenarioDisplayOptions.showCost, onChange: () => handleScenarioOptionToggle('showCost') }), _jsx("span", { className: "colored-label", style: { backgroundColor: SCENARIO_OPTION_COLORS.showCost }, children: "Cost" })] }) }), _jsx("div", { className: "dropdown-item", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: scenarioDisplayOptions.showDifficulty, onChange: () => handleScenarioOptionToggle('showDifficulty') }), _jsx("span", { className: "colored-label", style: { backgroundColor: SCENARIO_OPTION_COLORS.showDifficulty }, children: "Difficulty" })] }) }), _jsx("div", { className: "dropdown-item", children: _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: scenarioDisplayOptions.showReward, onChange: () => handleScenarioOptionToggle('showReward') }), _jsx("span", { className: "colored-label", style: { backgroundColor: SCENARIO_OPTION_COLORS.showReward }, children: "Reward" })] }) })] }))] }), _jsxs("button", { className: `view-toggle play-toggle ${mapViewMode === 'Play' ? 'active' : ''}`, onClick: handlePlayModeToggle, children: [_jsx(FaPlay, {}), " Play"] })] }) })] }), _jsxs("div", { className: "control-block zoom-block", children: [_jsxs("div", { className: "block-header", children: [_jsx("span", { children: "Zoom" }), _jsxs("span", { className: "zoom-value", children: [Math.round(transform.scale * 100), "%"] })] }), _jsx("div", { className: "block-content", children: _jsxs("div", { className: "zoom-controls", children: [_jsx("button", { onClick: handleZoomOut, className: "zoom-button", title: "Zoom Out", disabled: transform.scale <= MIN_ZOOM, children: _jsx(FaMinus, {}) }), _jsx("div", { className: "zoom-slider-container", children: _jsx(SliderInput, { min: 0, max: 1000, step: 10, value: zoomSliderValue, onChange: handleZoomSliderChange }) }), _jsx("button", { onClick: handleZoomIn, className: "zoom-button", title: "Zoom In", disabled: transform.scale >= MAX_ZOOM, children: _jsx(FaPlus, {}) })] }) })] })] }) })), activeToolbarTab === 'events' && (_jsx(EventEditor, { levelId: levelId, readOnly: readOnly, 
                                // Deleting detaches the event from map cells server-side;
                                // reload so the in-memory map can't save the stale refs back.
                                onEventsMutated: () => reloadFromBackend({ preserveHistory: true }), onUnsavedChangesChange: eventGuard.reportUnsaved, onRegisterSave: eventGuard.registerSave })), activeToolbarTab === 'ux-panels' && (_jsx(UxPanelEditor, { levelId: levelId, readOnly: readOnly, 
                                // Deleting strips the panel's actions from cell outcomes
                                // server-side; reload so the in-memory map stays truthful.
                                onPanelsMutated: () => reloadFromBackend({ preserveHistory: true }), onUnsavedChangesChange: uxPanelGuard.reportUnsaved, onRegisterSave: uxPanelGuard.registerSave })), activeToolbarTab === 'interactive' && (_jsx(ScenarioManager, { levelId: levelId, nodes: (_b = level.nodes) !== null && _b !== void 0 ? _b : {}, onUnsavedChangesChange: scenarioGuard.reportUnsaved, onRegisterSave: scenarioGuard.registerSave })), activeToolbarTab === 'missions' && (_jsx(MissionEditor, { levelId: levelId, onUnsavedChangesChange: missionGuard.reportUnsaved, onRegisterSave: missionGuard.registerSave })), activeToolbarTab === 'dialogues' && (_jsx(DialogueEditor, { levelId: levelId, onPendingChanges: dialogueGuard.reportUnsaved, onSaveChanged: dialogueGuard.registerSave })), activeToolbarTab === 'items' && (_jsx(ItemsManager, { levelId: levelId })), _jsx(Review, { levelId: levelId, visible: activeToolbarTab === 'review' })] }))] }), _jsxs("div", { className: "editor-body", children: [(activeToolbarTab === 'view' || (playLive === null || playLive === void 0 ? void 0 : playLive.active)) && (_jsx("div", { className: "main-panel", style: activeToolbarTab === 'view' ? undefined : { display: 'none' }, children: _jsxs("div", { className: `editor-viewport${balanceStampMode ? ' balance-stamping' : ''}`, ref: viewportRef, style: { cursor: getViewportCursor(), ['--stamp-cursor']: BALANCE_STAMP_CURSOR }, onMouseDown: handleMouseDown, onMouseMove: handleMouseMoveWrapper, onMouseLeave: () => { stopDragging(); setLastMousePos(null); }, onWheel: handleWheelZoom, children: [_jsxs("div", { className: "canvas-stage", ref: canvasStageRef, style: canvasStageStyle, children: [_jsx("div", { className: "map-background-layer", ref: mapBackgroundLayerRef, style: mapBackgroundLayerStyle }), _jsx("div", { className: "map-grid-layer", ref: mapGridLayerRef, style: mapGridLayerStyle, children: _jsx(MapGrid, { width: level.mapWidth, height: level.mapHeight, cellSize: scaledCellSize, selectedCoords: selectedNodeCoords, nodes: (_c = level.nodes) !== null && _c !== void 0 ? _c : {}, nodeTypes: nodeTypes, nodeStatuses: nodeStatuses, onNodeSelect: inPlayPreview ? () => { } : handleNodeSelect, overlayOpacity: overlayOpacity, overlayColor: overlayColor, lineWidth: Math.max(1, Math.round(lineWidth * transform.scale)), activeOverlays: mapOverlays, playMode: mapViewMode === 'Play', playSubMode: "Locks", locksOutcomeType: locksOutcomeType, levelId: levelId, events: levelEvents, scenarioDisplayOptions: scenarioDisplayOptions, highlightedCells: highlightedCells, playerPosition: inPlayPreview ? playLive === null || playLive === void 0 ? void 0 : playLive.position : undefined, visitedCells: inPlayPreview ? playLive === null || playLive === void 0 ? void 0 : playLive.visited : undefined, gameLockedCells: inPlayPreview ? playLive === null || playLive === void 0 ? void 0 : playLive.lockedCells : undefined, movableCells: inPlayPreview ? playLive === null || playLive === void 0 ? void 0 : playLive.movableCells : undefined, onPlayerMove: inPlayPreview ? (x, y) => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.move(getCoordinateString(x, y)); } : undefined }) })] }), showChecklist && !inPlayPreview && (_jsx(FloatingPanel, { title: "Portal checklist", initialViewportPosition: { x: 16, y: 160 }, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, parentViewportRef: viewportRef, isDraggable: true, attachToViewport: true, width: 300, showPinButton: false, onClose: () => setShowChecklist(false), children: _jsxs("div", { className: "checklist-scroll", children: [_jsx(OnboardingChecklist, { level: level, events: levelEvents, startTypeId: (_d = nodeTypes.find(t => t.name === 'Start')) === null || _d === void 0 ? void 0 : _d.id, verifyReport: verifyReport, onSubmit: handleSubmitForReview, submitting: submitting, onSubmitImages: handleSubmitImages, submittingImages: submittingImages, imagesError: imagesError }), level.moderationStatus !== ModerationStatus.Approved && (_jsx("div", { className: "checklist-verification", children: _jsx(VerificationPanel, { report: verifyReport, loading: verifying, onRerun: runVerify, onRepair: handleRepair, repairing: repairing, repairNote: repairNote }) }))] }) })), showDistribution && !inPlayPreview && (_jsx(FloatingPanel, { title: "Balance", initialViewportPosition: { x: 340, y: 300 }, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, parentViewportRef: viewportRef, isDraggable: true, attachToViewport: true, width: 340, showPinButton: false, onClose: () => { setBalanceStampMode(null); setShowDistribution(false); }, children: _jsx(DistributionPanel, { level: level, events: levelEvents, startTypeId: (_f = nodeTypes.find(t => t.name === 'Start')) === null || _f === void 0 ? void 0 : _f.id, readOnly: readOnly, stampMode: balanceStampMode, stampAbilities: balanceStampAbilities, stampAmounts: balanceStampAmounts, onStampModeChange: setBalanceStampMode, onStampAbilitiesChange: setBalanceStampAbilities, onStampAmountChange: (field, value) => setBalanceStampAmounts(prev => (Object.assign(Object.assign({}, prev), { [field]: value }))) }) })), selectedNodeCoords && editingNodeData && !inPlayPreview && (_jsx(FloatingPanel, { title: `Node ${getCoordinateString(selectedNodeCoords.x, selectedNodeCoords.y)}`, initialViewportPosition: { x: window.innerWidth - 360, y: 60 }, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, parentViewportRef: viewportRef, isDraggable: true, width: 320, height: 550, showPinButton: false, onClose: handleCloseNodePanel, children: _jsx(NodePropertiesForm, { coords: selectedNodeCoords, nodeData: editingNodeData, onUpdateNode: handleUpdateNodeData, levelId: levelId, nodes: (_g = level.nodes) !== null && _g !== void 0 ? _g : {}, mapWidth: level.mapWidth, mapHeight: level.mapHeight, onStartPhonePreview: handlePlayModeToggle, onHighlightCells: handleHighlightNodeAction, readOnly: readOnly }) })), mapViewMode === 'Play' && playSubMode === 'avatar' && (_jsx("div", { style: { display: inPlayPreview ? 'none' : undefined }, children: _jsx(FloatingPanel, { title: "Play with an Avatar", initialViewportPosition: { x: Math.max(20, window.innerWidth - 680), y: 55 }, initialAboveToolbar: true, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, parentViewportRef: viewportRef, isDraggable: true, attachToViewport: true, width: 640, showPinButton: false, onClose: () => { setPlayLive(null); setMapViewMode(null); }, children: _jsx(PlayMode, { levelId: levelId, nodes: level.nodes, events: levelEvents, mapWidth: level.mapWidth, mapHeight: level.mapHeight, startTypeId: (_h = nodeTypes.find(t => t.name === 'Start')) === null || _h === void 0 ? void 0 : _h.id, liftedMode: true, onPlayStateChange: setPlayLive, registerActions: (api) => { playApiRef.current = api; } }) }) })), inPlayPreview && (_jsxs(_Fragment, { children: [_jsx(PhonePreview, { levelId: levelId, locksOutcomeType: locksOutcomeType, selectedNodeCoords: null, nodes: (_j = level.nodes) !== null && _j !== void 0 ? _j : {}, onLocksOutcomeTypeChange: setLocksOutcomeType, 
                                            // Start with the phone's controls aligned to the top of the
                                            // Overlay/Zoom panels (like the agent picker at y:55) so the sound/
                                            // drag icons sit at the panel top and the whole phone fits below.
                                            initialPosition: { x: window.innerWidth - 340, y: 56 }, onClose: () => { setPlayLive(null); setMapViewMode(null); }, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, onHighlightCells: handleHighlightCells, playView: playLive, onAttempt: (ti, ai) => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.attempt(ti, ai); }, onMove: (coord) => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.move(coord); }, onScan: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.scan(); }, onTransmit: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.transmit(); }, onRestart: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.restart(); }, nodeTypes: nodeTypes, mapWidth: level.mapWidth, mapHeight: level.mapHeight, mapBackgroundUrl: (_k = level.backgroundImage) !== null && _k !== void 0 ? _k : undefined }), _jsxs("div", { className: "play-preview-bar", children: [_jsxs("span", { className: "ppb-stat", title: "Knowledge earned this run \u2014 the reward you collect for beating challenges", children: [_jsx("img", { src: KnowledgeReward, alt: "Knowledge", className: "ppb-stat-icon" }), " ", playLive === null || playLive === void 0 ? void 0 : playLive.knowledgeEarned] }), ((_m = (_l = playLive === null || playLive === void 0 ? void 0 : playLive.collectedResources) === null || _l === void 0 ? void 0 : _l.length) !== null && _m !== void 0 ? _m : 0) > 0 && (_jsx("span", { className: "ppb-resources", title: "Transmitted resources", children: playLive.collectedResources.map((r) => (_jsxs("span", { className: "ppb-res", title: `${r.name}: ${r.count}`, children: [r.iconUrl ? _jsx("img", { src: thumb(assetUrl(r.iconUrl), 48), alt: "" }) : null, r.count] }, r.resourceId))) })), (playLive === null || playLive === void 0 ? void 0 : playLive.status) === 'stranded' && _jsx("span", { className: "ppb-outcome lose", children: "Stranded" }), (playLive === null || playLive === void 0 ? void 0 : playLive.error) && _jsx("span", { className: "ppb-outcome lose", title: playLive.error, children: playLive.error }), _jsx("span", { className: "ppb-spacer" }), _jsx("button", { className: "ppb-btn ppb-icon", onClick: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.undo(); }, disabled: !(playLive === null || playLive === void 0 ? void 0 : playLive.canUndo) || (playLive === null || playLive === void 0 ? void 0 : playLive.loading), title: "Undo last step", children: _jsx(FaUndo, {}) }), _jsx("button", { className: "ppb-btn ppb-icon", onClick: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.redo(); }, disabled: !(playLive === null || playLive === void 0 ? void 0 : playLive.canRedo) || (playLive === null || playLive === void 0 ? void 0 : playLive.loading), title: "Redo", children: _jsx(FaRedo, {}) }), _jsxs("span", { className: "ppb-power", title: "Add energy to the run's budget (debug)", children: [_jsx("input", { className: "ppb-power-input", type: "text", inputMode: "numeric", value: powerAmount, onChange: (e) => { if (e.target.value === '' || /^\d+$/.test(e.target.value))
                                                                setPowerAmount(e.target.value); }, "aria-label": "Power to add" }), _jsx("button", { className: "ppb-btn ppb-power-add", onClick: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.addPower(parseInt(powerAmount, 10) || 0); }, disabled: (playLive === null || playLive === void 0 ? void 0 : playLive.loading) || !(parseInt(powerAmount, 10) > 0), children: "+ Power" })] }), _jsx("button", { className: "ppb-btn", onClick: () => { var _a; return (_a = playApiRef.current) === null || _a === void 0 ? void 0 : _a.restart(); }, disabled: playLive === null || playLive === void 0 ? void 0 : playLive.loading, children: "Restart" }), _jsx("button", { className: "ppb-btn ppb-exit", onClick: () => { setPlayLive(null); setMapViewMode(null); }, children: "Exit" })] })] })), false && showStampPanel && !inPlayPreview && (_jsx(FloatingPanel, { title: "Stamp Tool", initialViewportPosition: { x: 60, y: 150 }, zoomLevel: transform.scale, canvasTranslate: { x: transform.translateX, y: transform.translateY }, parentViewportRef: viewportRef, isDraggable: true, width: 320, height: 550, showPinButton: true, onClose: toggleStampPanel, children: _jsx(StampTool, { onApplyStamp: (stampConfig) => {
                                            // Save the stamp configuration to apply it when a node is clicked
                                            lastStampConfigRef.current = stampConfig;
                                            // If coordinates are already selected, apply the stamp immediately
                                            if (cursorType === 'stamp' && selectedNodeCoords) {
                                                handleApplyStamp(stampConfig, selectedNodeCoords);
                                            }
                                        }, onCursorChange: handleCursorChange, shouldReset: resetStampConfig, stampEnabled: cursorType === 'stamp' }) })), false && (_jsxs("div", { className: `stamp-button-container ${showStampActions ? 'expanded' : ''}`, style: { display: inPlayPreview ? 'none' : undefined }, children: [_jsxs("div", { className: `stamp-actions ${showStampActions ? 'visible' : ''}`, children: [_jsxs("div", { className: "action-buttons-container", children: [_jsx("button", { className: "button-icon reset-button", onClick: handleResetStamp, title: "Reset stamp configuration", children: _jsx(FaUndo, {}) }), _jsx("button", { className: "button-icon stamp-button", onClick: handleActivateStamp, title: cursorType === 'stamp' ? 'Deactivate stamp mode' : 'Activate stamp mode', children: _jsx(FaStamp, {}) })] }), _jsxs("div", { className: "action-labels-container", children: [_jsx("span", { className: "button-label", children: "Reset" }), _jsx("span", { className: "button-label", children: cursorType === 'stamp' ? 'Stop' : 'Start' })] })] }), _jsx("button", { className: `stamp-toggle-button ${showStampActions ? 'is-close' : ''} ${cursorType === 'stamp' ? 'active' : ''}`, onClick: toggleStampPanel, title: showStampPanel ? "Close stamp tool" : "Open stamp tool", children: showStampActions ? _jsx(FaTimes, {}) : _jsx(FaStamp, {}) })] }))] }) })), activeToolbarTab === 'art' && (_jsx("div", { className: "main-panel art-panel", children: _jsx(ArtManager, { levelId: levelId, levelWidth: level.mapWidth, levelHeight: level.mapHeight, nodes: (_o = level.nodes) !== null && _o !== void 0 ? _o : {}, onNodeImageChange: (x, y, url) => {
                                const coordString = getCoordinateString(x, y);
                                updateLevelState(draft => {
                                    if (!draft)
                                        return;
                                    if (!draft.nodes)
                                        draft.nodes = {};
                                    if (!draft.nodes[coordString]) {
                                        draft.nodes[coordString] = createDefaultNodeData();
                                    }
                                    draft.nodes[coordString].imageUrl = url || undefined;
                                });
                                // Mark node as dirty for save if needed
                                setDirtyNodes(prev => {
                                    const newSet = new Set(prev);
                                    newSet.add(coordString);
                                    return newSet;
                                });
                            } }) })), activeToolbarTab !== 'view' && activeToolbarTab !== 'art' && (_jsx("div", { className: "content-panel" }))] }), editorGuards.map((guard, index) => (_jsx(ConfirmationModal, { isOpen: guard.isModalOpen, onClose: guard.discard, onConfirm: guard.confirmSave, title: "Unsaved changes", message: (_jsxs("div", { className: "warning-message", children: [_jsxs("div", { className: "warning-header", children: ["Unsaved changes for \"", guard.unsavedName, "\""] }), _jsx("div", { className: "warning-footer", children: "Do you want to save them?" })] })), confirmText: "Save", cancelText: "Discard" }, index))), _jsx(Tutorial, { open: editorTourOpen, tour: EDITOR_TOUR, onClose: handleEditorTourClose }), _jsx(ConfirmationModal, { isOpen: resetConfirmOpen, onClose: () => { if (!resetting)
                    setResetConfirmOpen(false); }, onConfirm: handleResetToDefault, title: "Reset to default", message: (_jsxs("div", { className: "warning-message", children: [_jsxs("div", { className: "warning-header", children: ["Reset \"", level.name, "\" to its imported default?"] }), _jsxs("div", { className: "warning-footer", children: ["This rebuilds the whole level \u2014 cells, events, panels, links, images \u2014 from its source and ", _jsx("b", { children: "discards all your edits" }), ". This can't be undone."] })] })), confirmText: resetting ? 'Resetting…' : 'Reset', cancelText: "Cancel", isLoading: resetting })] }));
}
